import { useCallback, useEffect, useRef, useState } from 'react';
import { devLog } from '../utils/logger';

const STORAGE_KEY = 'wc_keep_awake';

const getStoredPreference = () => {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(STORAGE_KEY) === 'on';
};

export function useWakeLock() {
  const wakeLockRef = useRef(null);
  const mountedRef = useRef(true);
  const [enabled, setEnabled] = useState(getStoredPreference);
  const [active, setActive] = useState(false);
  const [supported, setSupported] = useState(() => typeof navigator !== 'undefined' && 'wakeLock' in navigator);
  const [error, setError] = useState('');

  const releaseLock = useCallback(async () => {
    const lock = wakeLockRef.current;
    wakeLockRef.current = null;
    if (lock) {
      try {
        await lock.release();
      } catch (err) {
        if (process.env.NODE_ENV === 'development') devLog.warn('Wake lock release failed:', err);
      }
    }
    if (mountedRef.current) setActive(false);
  }, []);

  const requestLock = useCallback(async () => {
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) {
      setSupported(false);
      setActive(false);
      setError('Not supported on this device');
      return false;
    }
    if (document.visibilityState !== 'visible') return false;
    if (wakeLockRef.current) return true;

    try {
      const lock = await navigator.wakeLock.request('screen');
      wakeLockRef.current = lock;
      setSupported(true);
      setActive(true);
      setError('');
      lock.addEventListener('release', () => {
        if (wakeLockRef.current === lock) wakeLockRef.current = null;
        if (mountedRef.current) setActive(false);
      });
      return true;
    } catch (err) {
      if (process.env.NODE_ENV === 'development') devLog.warn('Wake lock request failed:', err);
      if (mountedRef.current) {
        setActive(false);
        setError(err?.name === 'NotAllowedError' ? 'Check battery or browser settings' : 'Paused by device');
      }
      return false;
    }
  }, []);

  const toggle = useCallback(async () => {
    const next = !enabled;
    setEnabled(next);
    window.sessionStorage.setItem(STORAGE_KEY, next ? 'on' : 'off');
    if (next) await requestLock();
    else await releaseLock();
  }, [enabled, releaseLock, requestLock]);

  useEffect(() => {
    if (enabled) requestLock();
    else releaseLock();
  }, [enabled, releaseLock, requestLock]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && enabled) requestLock();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [enabled, requestLock]);

  useEffect(() => () => {
    mountedRef.current = false;
    if (wakeLockRef.current) wakeLockRef.current.release().catch(() => {});
  }, []);

  return { enabled, active, supported, error, toggle };
}

